import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Box,
  Alert,
  IconButton,
  Grid,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Switch,
  FormControlLabel,
  Tooltip
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { Add, Edit, Delete, Refresh, ExpandMore, Info } from '@mui/icons-material';
import axios from 'axios';
import RuleBuilder from '../components/RuleBuilder';
import { jsonToConditions } from '../utils/ruleConverter';

const FulfillmentRulesPage = () => {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [selectedRule, setSelectedRule] = useState(null);
  const [fulfillmentCenters, setFulfillmentCenters] = useState([]);
  const [ruleForm, setRuleForm] = useState({
    name: '',
    description: '',
    priority: 0,
    fulfillmentCenter: '',
    conditions: [{ field: '', operator: '', value: '', connector: 'and' }],
    isActive: true
  });

  useEffect(() => {
    fetchRules();
    fetchFulfillmentCenters();
  }, []);

  const fetchFulfillmentCenters = async () => {
    try {
      const response = await axios.get('/api/fulfillment/centers');
      setFulfillmentCenters(response.data.data || []);
    } catch (error) {
      console.error('Failed to fetch fulfillment centers:', error);
    }
  };

  const fetchRules = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/rules');
      setRules(response.data.rules || []);
    } catch (error) {
      console.error('Failed to fetch rules:', error);
      setError('Failed to load fulfillment rules');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateRule = () => {
    setSelectedRule(null);
    setRuleForm({
      name: '',
      description: '',
      priority: 0,
      fulfillmentCenter: '',
      conditions: [{ field: '', operator: '', value: '', connector: 'and' }],
      isActive: true
    });
    setShowForm(true);
  };

  const handleEditRule = (rule) => {
    setSelectedRule(rule);
    
    const fulfillmentCenterCodeMap = {};
    fulfillmentCenters.forEach(center => {
      fulfillmentCenterCodeMap[center.id] = center.code;
    });
    
    let conditions = [];
    if (rule.conditions && Array.isArray(rule.conditions)) {
      conditions = rule.conditions.map(condition => ({
        field: condition.field || '',
        operator: condition.operator || '',
        value: condition.operator === 'in' && Array.isArray(condition.value) 
          ? condition.value  // Keep array as-is for "in" operator
          : condition.value || '',
        connector: 'and'
      }));
    } else if (rule.conditions && typeof rule.conditions === 'object') {
      conditions = jsonToConditions(rule.conditions, 'fulfillment');
    }
    
    if (conditions.length === 0) {
      conditions = [{ field: '', operator: '', value: '', connector: 'and' }];
    }
    
    setRuleForm({
      name: rule.name || '',
      description: rule.description || '',
      priority: rule.priority || 0,
      fulfillmentCenter: fulfillmentCenterCodeMap[rule.fulfillment_center_id] || '',
      conditions: conditions,
      isActive: rule.is_active !== false
    });
    setShowForm(true);
  };

  const handleSaveRule = async () => {
    try {
      setError('');
      setSuccess('');
      
      const fulfillmentCenterMap = {};
      fulfillmentCenters.forEach(center => {
        fulfillmentCenterMap[center.code] = center.id;
      });

      const validConditions = ruleForm.conditions
        .filter(condition => {
          if (!condition.field || !condition.operator) return false;
          
          // For "in" operator, check if array has at least one value
          if (condition.operator === 'in') {
            return Array.isArray(condition.value) && condition.value.length > 0;
          }
          
          // For other operators, check if value is not empty
          return condition.value !== '' && condition.value !== null && condition.value !== undefined;
        })
        .map(condition => ({
          field: condition.field,
          operator: condition.operator,
          value: condition.value
        }));

      const ruleData = {
        name: ruleForm.name,
        description: ruleForm.description,
        priority: ruleForm.priority,
        fulfillment_center_id: fulfillmentCenterMap[ruleForm.fulfillmentCenter],
        conditions: validConditions,
        is_active: ruleForm.isActive
      };

      const endpoint = selectedRule ? `/api/rules/${selectedRule.id}` : '/api/rules';
      
      if (selectedRule) {
        await axios.put(endpoint, ruleData);
        setSuccess('Fulfillment rule updated successfully');
      } else {
        await axios.post(endpoint, ruleData);
        setSuccess('Fulfillment rule created successfully');
      }

      setShowForm(false);
      fetchRules();
      
    } catch (error) {
      console.error('❌ FulfillmentRulesPage: Save rule error:', error);
      setError('Failed to save fulfillment rule: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleDeleteRule = async (ruleId) => {
    if (!window.confirm('Are you sure you want to delete this fulfillment rule?')) {
      return;
    }

    try {
      setError('');
      await axios.delete(`/api/rules/${ruleId}`);
      setSuccess('Fulfillment rule deleted successfully');
      fetchRules();
    } catch (error) {
      console.error('❌ FulfillmentRulesPage: Delete rule error:', error);
      setError('Failed to delete fulfillment rule: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleToggleRule = async (ruleId, isActive) => {
    try {
      setError('');
      await axios.patch(`/api/rules/${ruleId}`, { is_active: !isActive });
      setSuccess(`Fulfillment rule ${!isActive ? 'activated' : 'deactivated'} successfully`);
      fetchRules();
    } catch (error) {
      console.error('❌ FulfillmentRulesPage: Toggle rule error:', error);
      setError('Failed to toggle fulfillment rule: ' + (error.response?.data?.error || error.message));
    }
  };

  const columns = [
    { field: 'name', headerName: 'Name', width: 200 },
    { field: 'priority', headerName: 'Priority', width: 100 },
    { 
      field: 'fulfillment_center_id', 
      headerName: 'Fulfillment Center', 
      width: 180,
      renderCell: (params) => {
        const center = fulfillmentCenters.find(fc => fc.id === params.value);
        return (
          <Typography variant="body2" sx={{ color: '#ffffff' }}>
            {center ? center.name : 'Unknown Center'}
          </Typography>
        );
      }
    },
    { 
      field: 'description', 
      headerName: 'Description', 
      width: 250,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>
          {params.value}
        </Typography>
      )
    },
    {
      field: 'is_active',
      headerName: 'Status',
      width: 120,
      renderCell: (params) => (
        <Tooltip title={`Click to ${params.value ? 'deactivate' : 'activate'}`}>
          <FormControlLabel
            control={
              <Switch
                checked={params.value}
                onChange={() => handleToggleRule(params.row.id, params.value)}
                color="primary"
                size="small"
              />
            }
            label={
              <Typography variant="caption" sx={{ color: params.value ? '#4caf50' : 'rgba(255, 255, 255, 0.6)' }}>
                {params.value ? 'Active' : 'Inactive'}
              </Typography>
            }
          />
        </Tooltip>
      )
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Tooltip title="Edit Rule">
            <IconButton 
              size="small" 
              onClick={() => handleEditRule(params.row)}
              sx={{ 
                color: '#64b5f6',
                '&:hover': { 
                  backgroundColor: 'rgba(100, 181, 246, 0.1)',
                  color: '#90caf9'
                }
              }}
            >
              <Edit fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete Rule">
            <IconButton 
              size="small" 
              onClick={() => handleDeleteRule(params.row.id)}
              sx={{ 
                color: '#f48fb1',
                '&:hover': { 
                  backgroundColor: 'rgba(244, 143, 177, 0.1)',
                  color: '#f8bbd9'
                }
              }}
            >
              <Delete fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      )
    }
  ];

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Accordion>
          <AccordionSummary
            expandIcon={<ExpandMore sx={{ color: '#ffffff' }} />}
            aria-controls="fulfillment-rules-info-content"
            id="fulfillment-rules-info-header"
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Info color="primary" />
              <Typography variant="h6" sx={{ color: '#ffffff' }}>How Fulfillment Rules Work</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body2" paragraph sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>
              <strong>Fulfillment Rules</strong> determine which fulfillment center should handle specific orders based on predefined conditions.
            </Typography>
            <Typography variant="body2" paragraph sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>
              <strong>Rule Priority:</strong> Rules are evaluated in priority order (lower numbers = higher priority). The first matching rule determines which fulfillment center will handle the order.
            </Typography>
            <Typography variant="body2" paragraph sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>
              <strong>Fulfillment Centers:</strong>
            </Typography>
            <Box component="ul" sx={{ pl: 2, mt: 1, color: 'rgba(255, 255, 255, 0.85)' }}>
              <li><strong>MetaKocka:</strong> Primary fulfillment center for standard orders</li>
              <li><strong>Eli Z:</strong> Secondary fulfillment center for specialized handling</li>
            </Box>
            <Typography variant="body2" paragraph sx={{ mt: 2, color: 'rgba(255, 255, 255, 0.9)' }}>
              <strong>Common Use Cases:</strong>
            </Typography>
            <Box component="ul" sx={{ pl: 2, mt: 1, color: 'rgba(255, 255, 255, 0.85)' }}>
              <li>Route orders by product type or SKU</li>
              <li>Direct high-value orders to specific centers</li>
              <li>Route orders by shipping destination</li>
              <li>Handle special product categories differently</li>
            </Box>
          </AccordionDetails>
        </Accordion>
      </Paper>

      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" component="h1" sx={{ color: '#ffffff' }}>
            Fulfillment Rules
          </Typography>
          <Box>
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={fetchRules}
              sx={{ mr: 1 }}
              disabled={loading}
            >
              Refresh
            </Button>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={handleCreateRule}
            >
              Create Fulfillment Rule
            </Button>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
            {success}
          </Alert>
        )}

        <Box sx={{ height: 600, width: '100%' }}>
          <DataGrid
            rows={rules}
            columns={columns}
            pageSize={10}
            rowsPerPageOptions={[10, 25, 50]}
            loading={loading}
            disableSelectionOnClick
            sx={{
              '& .MuiDataGrid-cell': {
                color: '#ffffff',
                borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
              },
              '& .MuiDataGrid-columnHeaders': {
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                color: '#ffffff',
                borderBottom: '1px solid rgba(255, 255, 255, 0.2)',
              },
              '& .MuiDataGrid-columnHeaderTitle': {
                color: '#ffffff',
                fontWeight: 600,
              },
              '& .MuiDataGrid-row': {
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.03)',
                },
              },
              '& .MuiDataGrid-footerContainer': {
                backgroundColor: 'rgba(255, 255, 255, 0.02)',
                color: '#ffffff',
                borderTop: '1px solid rgba(255, 255, 255, 0.1)',
              },
            }}
          />
        </Box>

        <Dialog open={showForm} onClose={() => setShowForm(false)} maxWidth="lg" fullWidth>
          <DialogTitle sx={{ color: '#ffffff' }}>
            {selectedRule ? 'Edit Fulfillment Rule' : 'Create Fulfillment Rule'}
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Rule Name"
                  value={ruleForm.name}
                  onChange={(e) => setRuleForm({ ...ruleForm, name: e.target.value })}
                  required
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Priority"
                  type="number"
                  value={ruleForm.priority}
                  onChange={(e) => setRuleForm({ ...ruleForm, priority: parseInt(e.target.value) || 0 })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required>
                  <InputLabel>Fulfillment Center</InputLabel>
                  <Select
                    value={ruleForm.fulfillmentCenter}
                    onChange={(e) => setRuleForm(prev => ({ ...prev, fulfillmentCenter: e.target.value }))}
                    label="Fulfillment Center"
                  >
                    {fulfillmentCenters.map((center) => (
                      <MenuItem key={center.id} value={center.code}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box 
                            sx={{ 
                              width: 12, 
                              height: 12, 
                              borderRadius: '50%', 
                              backgroundColor: center.code === 'metakocka' ? '#22c55e' : '#3b82f6'
                            }} 
                          />
                          {center.name}
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  multiline
                  rows={3}
                  value={ruleForm.description}
                  onChange={(e) => setRuleForm({ ...ruleForm, description: e.target.value })}
                />
              </Grid>
              <Grid item xs={12}>
                <RuleBuilder
                  value={ruleForm.conditions}
                  onChange={(conditions) => setRuleForm({ ...ruleForm, conditions })}
                  ruleType="fulfillment"
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowForm(false)}>Cancel</Button>
            <Button onClick={handleSaveRule} variant="contained">
              {selectedRule ? 'Update' : 'Create'}
            </Button>
          </DialogActions>
        </Dialog>
      </Paper>
    </Container>
  );
};

export default FulfillmentRulesPage;
